import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { writePrintDoc } from '../../utils/printUtils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, ClipboardList, Search, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '../../utils/excelExport';

const STATUS_BADGE = {
  draft:             'bg-slate-100 text-slate-700 border-slate-300',
  pending_approval:  'bg-amber-100 text-amber-700 border-amber-300',
  approved:          'bg-emerald-100 text-emerald-700 border-emerald-300',
  rejected:          'bg-rose-100 text-rose-700 border-rose-300',
};

const BusinessPlanReport = () => {
  const [plans, setPlans] = useState([]);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, eRes, bRes] = await Promise.all([
          api.get('/business-plans', { params: statusFilter !== 'all' ? { status: statusFilter } : {} }),
          api.get('/entrepreneurs', { params: { limit: 5000 } }),
          api.get('/branches'),
        ]);
        setPlans(pRes.data || []);
        setEntrepreneurs(eRes.data || []);
        setBranches(bRes.data || []);
      } catch { toast.error('Failed to load business plans'); }
      finally { setLoading(false); }
    };
    load();
  }, [statusFilter]);

  const entById = (id) => entrepreneurs.find(e => e.id === id);
  const brName = (id) => branches.find(b => b.id === id)?.name || '-';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return plans.filter(p => {
      if (branchFilter !== 'all' && p.branch_id !== branchFilter) return false;
      if (!q) return true;
      const e = entById(p.entrepreneur_id);
      return (e?.name || '').toLowerCase().includes(q) || (e?.enrollment_no || '').toLowerCase().includes(q);
    });
  }, [plans, branchFilter, search, entrepreneurs]);

  const handlePrint = () => {
    const rows = filtered.map((p, i) => {
      const e = entById(p.entrepreneur_id);
      return `<tr style="background:${i%2?'#f9fafb':'#fff'}"><td>${i+1}</td><td>${e?.enrollment_no||''}</td><td>${e?.name||''}</td><td>${brName(p.branch_id)}</td><td>${e?.enterprise?.name||''}</td><td style="text-align:right">Rs.${Number(p.proposed_loan_amount||0).toLocaleString('en-IN')}</td><td>${p.status}</td><td>${p.created_at?new Date(p.created_at).toLocaleDateString('en-IN'):''}</td></tr>`;
    }).join('');
    const html = `<!doctype html><html><head><title>Business Plans — WWAGS MIS</title>
    <style>body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}h1{color:#047857;font-size:20px;margin:0 0 4px}.meta{color:#64748b;font-size:12px;margin-bottom:14px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#047857;color:#fff;padding:8px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #e2e8f0}@media print{body{-webkit-print-color-adjust:exact}}</style>
    </head><body><h1>WWAGS MIS — Business Plans (1A)</h1>
    <p class="meta">Status: <b>${statusFilter}</b> · Branch: <b>${branchFilter==='all'?'All':brName(branchFilter)}</b> · Total: <b>${filtered.length}</b> · Generated: ${new Date().toLocaleString('en-IN')}</p>
    <table><thead><tr><th>#</th><th>Enrollment</th><th>Entrepreneur</th><th>Branch</th><th>Enterprise</th><th>Proposed Loan</th><th>Status</th><th>Created</th></tr></thead><tbody>${rows||'<tr><td colspan="8" style="text-align:center;color:#94a3b8;padding:20px">No business plans</td></tr>'}</tbody></table>
    </body></html>`;
    const w = window.open('', '_blank'); if (w) { writePrintDoc(w.document, html); setTimeout(() => w.print(), 500); }
  };

  const handleExcel = () => {
    if (!filtered.length) { toast.error('Nothing to export'); return; }
    const rows = filtered.map((p, i) => {
      const e = entById(p.entrepreneur_id);
      return {
        'S.No.': i + 1,
        'Enrollment': e?.enrollment_no || '',
        'Entrepreneur': e?.name || '',
        'Branch': brName(p.branch_id),
        'Enterprise': e?.enterprise?.name || '',
        'Operation Type': e?.enterprise?.operation_type || '',
        'Proposed Loan (Rs.)': Number(p.proposed_loan_amount || 0),
        'Repayment (months)': p.repayment_period_months || 0,
        'Own Investment': Number(p.own_investment || 0),
        'Bank Loan': Number(p.bank_loan || 0),
        'CEF Loan': Number(p.cef_loan || 0),
        'CIF / Others': Number(p.cif_others || 0),
        'Status': p.status,
        'Created': p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '',
        'Approved By': p.approved_by_email || '',
        'Approved On': p.approved_at ? new Date(p.approved_at).toLocaleDateString('en-IN') : '',
      };
    });
    exportToExcel({ filename: 'WWAGS_Business_Plans', sheetName: 'Business Plans', rows });
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2"><ClipboardList className="w-5 h-5 text-emerald-700" />1A. Business Plan Report</h1>
          <p className="text-sm text-slate-600">All business plans with status, branch and proposed loan amount.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs">Search</Label>
          <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} className="pl-8" placeholder="Name / enrollment" /></div>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending_approval">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Branch</Label>
          <Select value={branchFilter} onValueChange={setBranchFilter}><SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="text-sm"><span className="text-slate-500">Showing</span> <b>{filtered.length}</b> <span className="text-slate-500">of {plans.length} plans</span></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button onClick={handleExcel} className="bg-emerald-700 hover:bg-emerald-800 text-white"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-emerald-700 text-white sticky top-0"><tr>
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">Enrollment</th>
                <th className="px-2 py-2 text-left">Entrepreneur</th>
                <th className="px-2 py-2 text-left">Branch</th>
                <th className="px-2 py-2 text-left">Enterprise</th>
                <th className="px-2 py-2 text-right">Proposed Loan</th>
                <th className="px-2 py-2 text-center">Status</th>
                <th className="px-2 py-2 text-left">Created</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-400">No business plans found.</td></tr>
                ) : filtered.map((p, i) => {
                  const e = entById(p.entrepreneur_id);
                  return (
                    <tr key={p.id} className={i % 2 ? 'bg-slate-50' : ''}>
                      <td className="px-2 py-1.5">{i + 1}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{e?.enrollment_no || '-'}</td>
                      <td className="px-2 py-1.5 font-medium"><Link to={`/business-plans/${p.id}`} className="text-emerald-700 hover:underline">{e?.name || '-'}</Link></td>
                      <td className="px-2 py-1.5">{brName(p.branch_id)}</td>
                      <td className="px-2 py-1.5">{e?.enterprise?.name || '-'}</td>
                      <td className="px-2 py-1.5 text-right">Rs.{Number(p.proposed_loan_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-2 py-1.5 text-center"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] border capitalize ${STATUS_BADGE[p.status] || ''}`}>{p.status.replace('_', ' ')}</span></td>
                      <td className="px-2 py-1.5 text-[10px] text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessPlanReport;
